import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingBag, Timer } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "@/components/ui/sonner";
import api from "@/lib/api";
import { fcfa, ttc } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/motion";
import { useSeo } from "@/lib/seo";

const Countdown = ({ endAt }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, new Date(endAt).getTime() - now);
  const parts = [
    ["Jours", Math.floor(diff / 86400000)],
    ["Heures", Math.floor(diff / 3600000) % 24],
    ["Min", Math.floor(diff / 60000) % 60],
    ["Sec", Math.floor(diff / 1000) % 60],
  ];
  return (
    <div data-testid="promo-countdown" className="flex items-center gap-3">
      <Timer className="h-5 w-5 text-terracotta-light" />
      {parts.map(([label, v]) => (
        <div key={label} className="flex flex-col items-center border border-sand/20 bg-sand/5 px-4 py-3 backdrop-blur-sm">
          <span className="font-display text-3xl font-bold tabular-nums text-sand">{String(v).padStart(2, "0")}</span>
          <span className="text-[9px] font-semibold uppercase tracking-[0.25em] text-sand/50">{label}</span>
        </div>
      ))}
    </div>
  );
};

const PromoCard = ({ p, onOrder }) => (
  <Reveal>
    <div data-testid={`promo-card-${p.id}`} className="group flex flex-col border border-clay bg-cream transition-colors duration-300 hover:border-terracotta">
      <Link to={`/produit/${p.id}`} className="block aspect-[4/5] overflow-hidden bg-clay/40">
        <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover" />
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-terracotta">{p.category}</p>
        <Link to={`/produit/${p.id}`}>
          <h3 className="mt-1 font-display text-lg font-semibold leading-snug text-bark transition-colors hover:text-terracotta">{p.name}</h3>
        </Link>
        <p className="mt-2 font-display text-2xl font-bold text-terracotta">{fcfa(ttc(p))}</p>
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-bark/40">Prix TTC — tous frais inclus</p>
        <button
          data-testid={`promo-order-button-${p.id}`}
          onClick={() => onOrder(p)}
          className="mt-4 flex h-12 items-center justify-center gap-2 rounded-full bg-terracotta text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors duration-300 hover:bg-terracotta-dark"
        >
          <ShoppingBag className="h-4 w-4" /> Commander maintenant
        </button>
      </div>
    </div>
  </Reveal>
);

const Promo = () => {
  const [products, setProducts] = useState([]);
  const [promo, setPromo] = useState(null);
  const [loading, setLoading] = useState(true);
  const { add, setOpen } = useCart();

  useSeo({
    title: "Promotions — Lits coffres & cuisine",
    description: "Offres Teranga : lits coffres et robinetterie cuisine à prix doux. Livraison Dakar et partout au Sénégal, paiement Wave, Orange Money ou carte.",
    keywords: "promotion meubles Dakar, lit coffre pas cher Sénégal, mitigeur cuisine Dakar, promo salle de bain Sénégal",
    path: "/promo",
  });

  useEffect(() => {
    Promise.all([api.get("/products"), api.get("/settings/home")])
      .then(([p, h]) => {
        setProducts(p.data);
        setPromo(h.data.promo || null);
      })
      .catch(() => toast.error("Impossible de charger les promotions"))
      .finally(() => setLoading(false));
  }, []);

  const litsCoffres = useMemo(() => products.filter((p) => /lit/i.test(p.name)), [products]);
  const cuisine = useMemo(
    () => products.filter((p) => /mitigeur|robinet|mélangeur/i.test(p.name) && !/lit/i.test(p.name)),
    [products]
  );

  const promoActive = promo?.enabled && promo?.end_at && new Date(promo.end_at).getTime() > Date.now();

  const order = (p) => {
    add(p, 1);
    setOpen(true);
    toast.success(`${p.name} ajouté au panier`);
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-terracotta border-t-transparent" />
      </div>
    );
  }

  return (
    <div data-testid="promo-page" className="bg-sand pb-24">
      {/* BANNIÈRE PROMOTIONNELLE */}
      <section data-testid="promo-banner" className="relative overflow-hidden bg-bark text-sand">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="mb-6 text-xs font-semibold uppercase tracking-[0.35em] text-terracotta-light"
          >
            Offres Teranga — pour une durée limitée
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl"
          >
            {promoActive && promo.title ? promo.title : (
              <>Lits coffres & cuisine :<br /><span className="italic text-terracotta-light">les essentiels à prix doux</span></>
            )}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-6 max-w-xl text-sm leading-relaxed text-sand/70 sm:text-base"
          >
            {promoActive && promo.subtitle
              ? promo.subtitle
              : "Meublez votre chambre et équipez votre cuisine avec nos pièces phares. Prix TTC, tous frais inclus — livraison à Dakar et dans les autres villes du Sénégal."}
          </motion.p>
          {promoActive && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.5 }}
              className="mt-10"
            >
              <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.3em] text-sand/50">L'offre se termine dans</p>
              <Countdown endAt={promo.end_at} />
            </motion.div>
          )}
        </div>
        <p className="pointer-events-none absolute -bottom-6 right-0 select-none whitespace-nowrap font-display text-[14vw] font-bold leading-none text-sand/5">
          promo
        </p>
      </section>

      {/* LITS COFFRES */}
      {litsCoffres.length > 0 && (
        <section data-testid="promo-section-lits" className="mx-auto max-w-7xl px-6 pt-20 lg:px-10">
          <Reveal>
            <div className="flex items-end justify-between">
              <div>
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-terracotta">Rangements malins</p>
                <h2 className="font-display text-3xl font-semibold tracking-tight text-bark sm:text-4xl">Lits Coffres</h2>
              </div>
              <Link to="/boutique?categorie=Meubles" className="group hidden items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-bark/50 transition-colors hover:text-terracotta sm:flex">
                Tous les meubles <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {litsCoffres.map((p) => <PromoCard key={p.id} p={p} onOrder={order} />)}
          </div>
        </section>
      )}

      {/* CUISINE */}
      {cuisine.length > 0 && (
        <section data-testid="promo-section-cuisine" className="mx-auto max-w-7xl px-6 pt-20 lg:px-10">
          <Reveal>
            <div className="flex items-end justify-between">
              <div>
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-terracotta">Mitigeurs & robinetterie</p>
                <h2 className="font-display text-3xl font-semibold tracking-tight text-bark sm:text-4xl">Cuisine</h2>
              </div>
              <Link to="/boutique?categorie=Plomberie" className="group hidden items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-bark/50 transition-colors hover:text-terracotta sm:flex">
                Toute la robinetterie <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {cuisine.map((p) => <PromoCard key={p.id} p={p} onOrder={order} />)}
          </div>
        </section>
      )}

      {litsCoffres.length === 0 && cuisine.length === 0 && (
        <p data-testid="promo-empty" className="mx-auto max-w-7xl px-6 pt-20 text-center text-sm text-bark/50 lg:px-10">
          Les promotions reviennent très bientôt — en attendant, découvrez toute la boutique.
        </p>
      )}
    </div>
  );
};

export default Promo;
